import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@/types'
import { api } from '@/services/api'
import { toast } from 'sonner'
import i18n from '@/lib/i18n'

interface AuthState {
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean
    isDeveloper: boolean
    login: () => void
    logout: () => Promise<void>
    checkAuth: () => Promise<boolean>
}

let authCheckPromise: Promise<boolean> | null = null

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            isAuthenticated: false,
            isLoading: true,
            isDeveloper: false,

            login: () => {
                window.location.href = api.auth.getDiscordLoginUrl()
            },

            logout: async () => {
                try {
                    await api.auth.logout()
                    toast.success(i18n.t('login.loggedOut'))
                } catch (error) {
                    const message =
                        error instanceof Error
                            ? error.message
                            : i18n.t('login.logoutFailed')
                    toast.error(message)
                } finally {
                    set({
                        user: null,
                        isAuthenticated: false,
                        isDeveloper: false,
                    })
                }
            },

            checkAuth: async () => {
                if (authCheckPromise) {
                    return authCheckPromise
                }

                authCheckPromise = (async () => {
                    const currentState = get()
                    if (!currentState.isAuthenticated) {
                        set({ isLoading: true })
                    }

                    try {
                        const response = await api.auth.checkStatus()

                        if (response.data.authenticated && response.data.user) {
                            const user = response.data.user
                            set({
                                user,
                                isAuthenticated: true,
                                isLoading: false,
                                isDeveloper: Boolean(user.isDeveloper),
                            })

                            return true
                        } else {
                            set({
                                user: null,
                                isAuthenticated: false,
                                isLoading: false,
                                isDeveloper: false,
                            })
                            return false
                        }
                    } catch {
                        set({
                            user: null,
                            isAuthenticated: false,
                            isLoading: false,
                            isDeveloper: false,
                        })
                        return false
                    } finally {
                        // Clear synchronously on settle: concurrent callers
                        // share the in-flight promise, and the next call after
                        // settle performs a fresh check, no timer window that
                        // serves a stale result (#1186).
                        authCheckPromise = null
                    }
                })()

                return authCheckPromise
            },
        }),
        {
            name: 'lucky-auth',
            partialize: (state) => ({
                user: state.user,
                isAuthenticated: state.isAuthenticated,
                isDeveloper: state.isDeveloper,
            }),
        },
    ),
)
